import { put, call } from "redux-saga/effects";
import * as actions from "./actions";
import * as api from "./api";
import { generateDelay } from "../shared/generators";

// AI-assist poll cadence: 2 s ticks (matching the GM AiChatWindow), bounded so a stuck job
// cannot poll forever if the player leaves the sheet open (§6.5).
const AI_ASSIST_POLL_INTERVAL = 2000;
const AI_ASSIST_MAX_POLLS = 30;
const AI_ASSIST_PENDING = ["queued", "inferring"];

// Stores the session returned in the response body directly - the dedicated GET plus
// every mutation's response body are the source of truth, no game/state re-pull needed.
function* request(actionPair, apiFn, payload) {
  try {
    const response = yield call(apiFn, payload);
    yield put(actionPair.success(response.data));
  } catch (error) {
    yield put(actionPair.error(error));
    // A 409 means the client is behind the session's true state (stale comparison_id,
    // wrong step, or a backtrack attempt at the floor). Re-sync from the authoritative
    // GET so the UI advances to the real current step instead of wedging on a stale
    // request the backend keeps rejecting.
    const status = error && error.response && error.response.status;
    if (status === 409 && actionPair !== actions.session.get) {
      yield put(actions.session.get.request());
    }
  }
}

export function* getSession() {
  yield* request(actions.session.get, api.session.get);
}

export function* comparisonGenerality(action) {
  yield* request(actions.comparisons.generality, api.comparisons.generality, action.payload);
}

export function* comparisonOutcome(action) {
  yield* request(actions.comparisons.outcome, api.comparisons.outcome, action.payload);
}

export function* analysisObservation(action) {
  yield* request(actions.analysis.observation, api.analysis.observation, action.payload);
}

export function* analysisSeriousness(action) {
  yield* request(actions.analysis.seriousness, api.analysis.seriousness, action.payload);
}

export function* analysisIssues(action) {
  yield* request(actions.analysis.issues, api.analysis.issues, action.payload);
}

export function* analysisRisk(action) {
  yield* request(actions.analysis.risk, api.analysis.risk, action.payload);
}

export function* analysisSolution(action) {
  yield* request(actions.analysis.solution, api.analysis.solution, action.payload);
}

export function* analysisContinue(action) {
  yield* request(actions.analysis.continue, api.analysis.continue, action.payload);
}

export function* analysisRiskMitigation(action) {
  yield* request(actions.analysis.riskMitigation, api.analysis.riskMitigation, action.payload);
}

export function* analysisSolutionMaximizer(action) {
  yield* request(
    actions.analysis.solutionMaximizer, api.analysis.solutionMaximizer, action.payload,
  );
}

export function* analysisGoal(action) {
  yield* request(actions.analysis.goal, api.analysis.goal, action.payload);
}

export function* analysisBack() {
  yield* request(actions.analysis.back, api.analysis.back);
}

// Polls the step's assist result every 2 s until it settles (or the bound is hit).
function* pollAssistUntilSettled(step, initialStatus) {
  let status = initialStatus;
  let polls = 0;
  while (AI_ASSIST_PENDING.includes(status) && polls < AI_ASSIST_MAX_POLLS) {
    yield call(generateDelay, AI_ASSIST_POLL_INTERVAL);
    const pollResponse = yield call(api.aiAssist.poll, { step });
    yield put(actions.aiAssist.poll.success({ step, ...pollResponse.data }));
    status = pollResponse.data.aiAssist && pollResponse.data.aiAssist.status;
    polls += 1;
  }

  if (AI_ASSIST_PENDING.includes(status)) {
    yield put(actions.aiAssist.poll.error({ step, error: new Error("ai_assist_timeout") }));
  }
}

// Graduated AI step companion (§6.5): POST the step's draft at its next ladder level, then
// poll the async result. The player's text is never touched here — even final ideas are
// rendered read-only next to the input.
export function* aiAssistRequest(action) {
  const { step, draft } = action.payload;
  try {
    const response = yield call(api.aiAssist.request, { step, draft });
    yield put(actions.aiAssist.request.success({ step, ...response.data }));
    yield* pollAssistUntilSettled(step, response.data.aiAssist && response.data.aiAssist.status);
  } catch (error) {
    yield put(actions.aiAssist.request.error({ step, error }));
  }
}

// Restores a step's latest assist result after a reload/rejoin (the server keeps the chat).
// Resumes the poll loop if the restored request is still inferring.
export function* aiAssistRestore(action) {
  const { step } = action.payload;
  try {
    const response = yield call(api.aiAssist.poll, { step });
    yield put(actions.aiAssist.poll.success({ step, ...response.data }));
    yield* pollAssistUntilSettled(step, response.data.aiAssist && response.data.aiAssist.status);
  } catch (error) {
    yield put(actions.aiAssist.poll.error({ step, error }));
  }
}
